import { supabase } from '@/lib/supabase';
import { Sale, RepairJob, Purchase } from '@/types/models';

export interface DashboardStatsOwner {
  todaySalesCount: number;
  todayRevenue: number;
  monthRevenue: number;
  grossProfit: number;
  netProfit: number;
  laptopsInStock: number;
  inventoryCostValue: number;
  inventoryRetailValue: number;
  lowStockCount: number;
  activeRepairsCount: number;
  recentSales: Sale[];
  recentPurchases: Purchase[];
  recentRepairs: RepairJob[];
}

export interface DashboardStatsStaff {
  laptopsInStock: number;
  activeRepairsCount: number;
  lowStockCount: number;
  recentSales: Array<{
    id: string;
    invoice_number: string;
    sale_date: string;
    total: number;
    customer_name: string;
  }>;
  recentRepairs: Array<{
    id: string;
    job_number: string;
    device_label: string;
    status: string;
    priority: string;
  }>;
}

export async function fetchOwnerDashboardStats(): Promise<DashboardStatsOwner> {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 7)}-01`;
  const tomorrow = new Date(Date.parse(`${today}T00:00:00Z`) + 86400000).toISOString();

  // 1. Today's gross sales, bounded to the UTC day used by the database.
  // Page explicitly so the API row limit cannot silently understate revenue.
  let todaySalesCount = 0;
  let todayRevenue = 0;
  for (let from = 0; ; from += 1000) {
    const { data: todaySales, error: todaySalesError } = await supabase
      .from('sales').select('id, total')
      .gte('sale_date', `${today}T00:00:00Z`).lt('sale_date', tomorrow)
      .in('status', ['completed', 'partially_refunded', 'refunded'])
      .order('id', { ascending: true }).range(from, from + 999);
    if (todaySalesError) throw todaySalesError;
    const page = todaySales ?? [];
    todaySalesCount += page.length;
    todayRevenue += page.reduce((acc, sale) => acc + Number(sale.total), 0);
    if (page.length < 1000) break;
  }

  // 2. Month's PnL View
  const { data: pnlData, error: pnlDataError } = await supabase
    .from('monthly_pnl')
    .select('*')
    .eq('month', monthStart)
    .limit(1);
  if (pnlDataError) throw pnlDataError;

  const monthPnl = pnlData?.[0];
  const monthRevenue = Number(monthPnl?.net_revenue) || 0;
  const grossProfit = Number(monthPnl?.gross_profit) || 0;
  const netProfit = Number(monthPnl?.net_profit) || 0;

  // 3. Stock Summary View
  const { data: stockSummary, error: stockSummaryError } = await supabase.from('stock_summary').select('*');
  if (stockSummaryError) throw stockSummaryError;
  const inStockRow = (stockSummary ?? []).find((s) => s.status === 'in_stock');
  const laptopsInStock = Number(inStockRow?.laptop_count) || 0;
  const heldStock = (stockSummary ?? []).filter(s => ['in_stock', 'reserved', 'returned', 'under_repair', 'faulty'].includes(s.status));
  const inventoryCostValue = heldStock.reduce((acc, s) => acc + (Number(s.cost_value) || 0), 0);
  const inventoryRetailValue = heldStock.reduce((acc, s) => acc + (Number(s.retail_value) || 0), 0);

  // 4. Low Stock Accessories View
  const { count: lowStockCount, error: lowStockError } = await supabase.from('low_stock_accessories').select('id', { count: 'exact', head: true });
  if (lowStockError) throw lowStockError;

  // 5. Active Repairs
  const { count: activeRepairsCount, error: activeRepairsError } = await supabase
    .from('repair_jobs')
    .select('id', { count: 'exact', head: true })
    .not('status', 'in', '("returned","cancelled","cannot_repair")');
  if (activeRepairsError) throw activeRepairsError;


  // 6. Recent Sales
  const { data: recentSales, error: recentSalesError } = await supabase
    .from('sales')
    .select('*, customer:customers(id, name)')
    .order('sale_date', { ascending: false })
    .order('id', { ascending: false })
    .limit(5);
  if (recentSalesError) throw recentSalesError;

  // 7. Recent Purchases
  const { data: recentPurchases, error: recentPurchasesError } = await supabase
    .from('purchases')
    .select('*, supplier:suppliers(id, name)')
    .order('purchase_date', { ascending: false })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(5);
  if (recentPurchasesError) throw recentPurchasesError;

  // 8. Recent Repairs
  const { data: recentRepairs, error: recentRepairsError } = await supabase
    .from('repair_jobs')
    .select('*, customer:customers(id, name), customer_device:customer_devices(*), inventory_laptop:laptops(*)')
    .order('received_at', { ascending: false })
    .limit(5);
  if (recentRepairsError) throw recentRepairsError;

  return {
    todaySalesCount,
    todayRevenue,
    monthRevenue,
    grossProfit,
    netProfit,
    laptopsInStock,
    inventoryCostValue,
    inventoryRetailValue,
    lowStockCount: lowStockCount ?? 0,
    activeRepairsCount: activeRepairsCount ?? 0,
    recentSales: (recentSales ?? []) as Sale[],
    recentPurchases: (recentPurchases ?? []) as Purchase[],
    recentRepairs: (recentRepairs ?? []) as RepairJob[],
  };
}

export async function fetchStaffDashboardStats(): Promise<DashboardStatsStaff> {
  // Safe staff catalog reads
  const { data: catRepairs, error: catRepairsError } = await supabase.rpc('get_repairs_catalog');
  if (catRepairsError) throw catRepairsError;
  const activeRepairs = (catRepairs ?? []).filter(
    (r: any) => !['returned', 'cancelled', 'cannot_repair'].includes(r.status)
  );

  const { data: laptopsData, error: laptopsDataError } = await supabase.rpc('get_inventory_catalog');
  if (laptopsDataError) throw laptopsDataError;
  const laptopsInStock = (laptopsData ?? []).filter((l: any) => l.status === 'in_stock').length;

  const { data: lowStockData, error: lowStockDataError } = await supabase.rpc('get_accessories_catalog');
  if (lowStockDataError) throw lowStockDataError;
  const lowStockCount = (lowStockData ?? []).filter((a: any) => Number(a.quantity) <= Number(a.reorder_level)).length;

  const { data: salesData, error: salesDataError } = await supabase
    .from('sales')
    .select('id, invoice_number, sale_date, total, customer:customers(name)')
    .order('sale_date', { ascending: false })
    .order('id', { ascending: false })
    .limit(5);
  if (salesDataError) throw salesDataError;

  const recentSales = (salesData ?? []).map((s: any) => ({
    id: s.id,
    invoice_number: s.invoice_number,
    sale_date: s.sale_date,
    total: s.total,
    customer_name: s.customer?.name ?? 'Walk-in Customer',
  }));

  const recentRepairs = (catRepairs ?? []).slice(0, 5).map((r: any) => ({
    id: r.id,
    job_number: r.job_number,
    device_label: r.device_label,
    status: r.status,
    priority: r.priority,
  }));

  return {
    laptopsInStock,
    activeRepairsCount: activeRepairs.length,
    lowStockCount,
    recentSales,
    recentRepairs,
  };
}
